import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { Layout } from './components/layout.js'
import { Alert } from './components/ui/feedback.js'
import { t } from './i18n/index.js'
import { AttributesPage } from './pages/attributes.js'
import { TenantsPage } from './pages/tenants.js'
import { EventsPage } from './pages/events.js'
import { ItemDetailPage } from './pages/item-detail.js'
import { ItemsPage } from './pages/items.js'
import { LoginPage } from './pages/login.js'
import { PendingPage } from './pages/pending.js'
import { StatusPage } from './pages/status.js'
import { UsersPage } from './pages/users.js'
import { ValueListDetailPage } from './pages/value-list-detail.js'
import { ExportsPage } from './pages/exports.js'
import { ValueListsPage } from './pages/value-lists.js'
import { useAuth } from './lib/auth.js'

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => (
    <div className="p-8">
      <Alert tone="warning">{t().errors.notFound}</Alert>
    </div>
  ),
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

/**
 * Everything below this route requires a session.
 *
 * While the initial silent refresh is still running, it renders a placeholder
 * rather than redirecting: sending a signed-in user to the login screen for a
 * few hundred milliseconds after every reload looks like a bug, and it is one.
 */
function AuthenticatedLayout() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return <p className="text-text-muted p-8 text-sm">{t().app.loading}</p>
  }

  if (!user) {
    return <Navigate to="/login" />
  }

  return <Layout />
}

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: AuthenticatedLayout,
})

const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  beforeLoad: () => {
    // TanStack Router signals a redirect by throwing a marker object rather
    // than an Error; that is the library's contract, not a mistake.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/status' })
  },
  component: StatusPage,
})

const statusRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/status',
  component: StatusPage,
})

/**
 * Admin-only in the interface. The real enforcement is server-side — this only
 * spares a capturer a screen full of 403s.
 */
/** The platform role only. A team lead never learns other tenants exist. */
function PlatformOnly({ children }: { children: React.ReactNode }) {
  const { hasRole } = useAuth()

  if (!hasRole('platform')) {
    return <Alert tone="warning">{t().errors.forbidden}</Alert>
  }

  return <>{children}</>
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { hasRole } = useAuth()

  if (!hasRole('admin')) {
    return <Alert tone="warning">{t().errors.forbidden}</Alert>
  }

  return <>{children}</>
}

const usersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/users',
  component: () => (
    <AdminOnly>
      <UsersPage />
    </AdminOnly>
  ),
})

const valueListsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/stammdaten',
  component: () => (
    <AdminOnly>
      <ValueListsPage />
    </AdminOnly>
  ),
})

const valueListDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/stammdaten/$listId',
  component: () => (
    <AdminOnly>
      <ValueListDetailPage />
    </AdminOnly>
  ),
})

const tenantsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/mandanten',
  component: () => (
    <PlatformOnly>
      <TenantsPage />
    </PlatformOnly>
  ),
})

const eventsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/messen',
  component: () => (
    <AdminOnly>
      <EventsPage />
    </AdminOnly>
  ),
})

const itemsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/artikel',
  // A fair's card links here with its id. Without this the parameter would be
  // dropped and the link would promise a filter it never applies.
  validateSearch: (search: Record<string, unknown>): { eventId?: string } =>
    typeof search['eventId'] === 'string' ? { eventId: search['eventId'] } : {},
  component: ItemsPage,
})

// Declared before the $itemId route. The other way round, "neu" is read as an
// id and the detail view asks the server for an article by that name.
const itemCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/artikel/neu',
  component: () => (
    <AdminOnly>
      <ItemDetailPage mode="create" />
    </AdminOnly>
  ),
})

const itemDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/artikel/$itemId',
  component: () => <ItemDetailPage mode="view" />,
})

const attributesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/felder',
  component: () => (
    <AdminOnly>
      <AttributesPage />
    </AdminOnly>
  ),
})

const exportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/export',
  component: ExportsPage,
})

const pendingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/zu-pruefen',
  component: () => (
    <AdminOnly>
      <PendingPage />
    </AdminOnly>
  ),
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([
    indexRoute,
    statusRoute,
    usersRoute,
    valueListsRoute,
    valueListDetailRoute,
    tenantsRoute,
    eventsRoute,
    itemsRoute,
    itemCreateRoute,
    itemDetailRoute,
    attributesRoute,
    pendingRoute,
    exportsRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
